import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import type { ReactNode } from 'react';
import DialogComponent from './dialog.component';
import DialogFormContentSurface, { getDialogFormBodyContentSx } from './dialog-form-content-surface.component';
import LoadingOverlayButton from '../button/loading-overlay-button.component';
import CommonInput from '../form/common-input.component';
import type { Option } from '../form/select-input-multiple';
import i18n from '../../i18n';

/** 弹窗内多选下拉的候选项 */
export type MultiSelectTagOption = {
    value: number;
    label: string;
};

export type MultiSelectTagDialogProps = {
    open: boolean;
    title: string;
    /**
     * 多选框 `label`：须为 i18n 文案 key 字符串（与行内 `CommonInput` 一致）；
     * `SelectInputMultiple` 会对 string 再执行 `i18n.t`，勿传已翻译长句。
     */
    selectLabel: string | ReactNode;
    options: MultiSelectTagOption[];
    draftIds: number[];
    onDraftChange: (ids: number[]) => void;
    onClose: () => void;
    onSave: () => void | Promise<void>;
    saveLoading: boolean;
    disabled?: boolean;
};

/** 将已选 id 同步为 `SELECT_MUL` 所需的 `Option[]` */
function idsToOptions(ids: number[], source: MultiSelectTagOption[]): Option[] {
    const idSet = new Set(ids.map((n) => Number(n)));
    return source
        .filter((o) => idSet.has(Number(o.value)))
        .map((o) => ({ value: String(o.value), label: o.label }));
}

/**
 * 多选弹窗：内容区为 `CommonInput` + `SELECT_MUL`（含全选），页脚取消 + 保存。
 * 纸面：`heightSize="small"` 固定高度；`DialogFormContentSurface` 提供 `fill-neutral-100` 浅底衬，`getDialogFormBodyContentSx('center')` 在 `DialogContent` 内竖直居中多选框。
 */
export default function MultiSelectTagDialog(props: MultiSelectTagDialogProps) {
    const { open, title, selectLabel, options, draftIds, onDraftChange, onClose, onSave, saveLoading, disabled = false } = props;

    const mulOpts: Option[] = useMemo(
        () => options.map((m) => ({ value: String(m.value), label: m.label })),
        [options]
    );
    const selectValue = useMemo(() => idsToOptions(draftIds, options), [draftIds, options]);
    const busy = saveLoading || disabled;

    return (
        <DialogComponent
            maxWidth="sm"
            heightSize="small"
            open={open}
            onClose={() => {
                if (busy) {
                    return;
                }
                onClose();
            }}
            defaultHeaderProps={{
                title,
                onClose: () => {
                    if (!busy) {
                        onClose();
                    }
                },
            }}
            sx={getDialogFormBodyContentSx('center')}
            dialogContentStyle={{ paddingTop: 0 }}
            footer={
                <div>
                    <Button
                        type="button"
                        color="primary"
                        variant="outlined"
                        disabled={saveLoading}
                        onClick={() => {
                            if (!busy) {
                                onClose();
                            }
                        }}
                    >
                        {i18n.t('google_text_public_button_cancel')}
                    </Button>
                    <Box marginLeft={4} display="inline-block">
                        <LoadingOverlayButton
                            type="button"
                            color="primary"
                            variant="contained"
                            loading={saveLoading}
                            loadingIndicatorSize={24}
                            onClick={() => {
                                void onSave();
                            }}
                        >
                            {i18n.t('general_text_save')}
                        </LoadingOverlayButton>
                    </Box>
                </div>
            }
        >
            <DialogFormContentSurface fullHeight>
                <CommonInput
                    inputType="SELECT_MUL"
                    fullWidth
                    disabled={busy}
                    label={selectLabel}
                    value={selectValue as never}
                    multipleProps={{
                        options: mulOpts,
                        fullSelect: true,
                        onChange: (opts: Option[]) => {
                            onDraftChange(opts.map((o) => Number(o.value)));
                        },
                    }}
                />
            </DialogFormContentSurface>
        </DialogComponent>
    );
}
